import { SiemProtocol } from '@kirin/api-client/contracts/settings';
import type { FieldErrors, Resolver, ResolverResult } from 'react-hook-form';

import type { SiemFormValues } from './types';

function isValidHecUrl(value: string): boolean {
  try {
    // HEC must be HTTPS — the token is a bearer secret, so http would put it on
    // the wire in cleartext (KIR-3839). Splunk HEC is HTTPS anyway.
    return new URL(value).protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Protocol-conditional validation for the SIEM connect form (KIR-4032):
 *   - HEC: `endpointUrl` (valid HTTPS URL) + `hecToken` required.
 *   - syslog (tcp/udp): `host` + `port` (1–65535) required.
 * Only the active protocol's fields are validated, so switching protocols does
 * not surface errors for the hidden fields.
 */
export const siemFormResolver: Resolver<SiemFormValues> = async values => {
  const errors: FieldErrors<SiemFormValues> = {};

  if (values.protocol === SiemProtocol.Hec) {
    if (!values.endpointUrl || values.endpointUrl.trim().length === 0) {
      errors.endpointUrl = {
        type: 'required',
        message: 'Endpoint URL is required',
      };
    } else if (!isValidHecUrl(values.endpointUrl)) {
      errors.endpointUrl = {
        type: 'validate',
        message: 'HEC endpoint must be an HTTPS URL',
      };
    }
    if (!values.hecToken || values.hecToken.trim().length === 0) {
      errors.hecToken = { type: 'required', message: 'HEC token is required' };
    }
  } else {
    if (!values.host || values.host.trim().length === 0) {
      errors.host = { type: 'required', message: 'Host is required' };
    }
    if (
      !Number.isFinite(values.port) ||
      values.port < 1 ||
      values.port > 65535
    ) {
      errors.port = { type: 'validate', message: 'Invalid Port' };
    }
  }

  return { values, errors } as ResolverResult<SiemFormValues>;
};
